import {
  approvalDecisions,
  approvalTaskType,
  formTypes,
  hasSubQuestions,
} from "../constants/page";

export function getFormattedDate(date) {
  const year = date.getFullYear();

  let month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : `0${month}`;

  let day = date.getDate().toString();
  day = day.length > 1 ? day : `0${day}`;

  return `${month}/${day}/${year}`;
}

export function validateEmail(email) {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
}

export const formTypeOptions = (permissions) => {
  return [
    {
      groupName: "Reporting",
    },
    ...(permissions.canViewSubmittedReports
      ? [
          {
            key: formTypes.incidentReport,
            value: "Incident Reporting",
          },
        ]
      : []),
    ...(permissions.canViewAuditPage
      ? [
          {
            key: formTypes.auditReport,
            value: "Audit Reporting",
          },
        ]
      : []),
    ...(true
      ? [
          {
            key: formTypes.maintenanceRequests,
            value: "Maintenance Requests",
          },
        ]
      : []),
    ...(permissions.r1
      ? [
          {
            key: formTypes.itTicketing,
            value: "Support Tickets",
          },
        ]
      : []),
    ...(permissions.r3
      ? [
          {
            key: formTypes.surveys,
            value: "Surveys",
          },
        ]
      : []),
    ...(permissions.r4
      ? [
          {
            key: formTypes.other,
            value: "Other",
          },
        ]
      : []),
    ...(true // add for asset access
      ? [
          {
            groupName: "Assets",
          },
          {
            key: formTypes.pm,
            value: "Preventative Maintenance",
          },
          {
            key: formTypes.workOrders,
            value: "Work Orders",
          },
          {
            key: formTypes.performance,
            value: "Performance",
          },
          {
            key: formTypes.assetOther,
            value: "Other",
          },
        ]
      : []),
  ];
};

function sameUuid(left, right) {
  if (left == null || right == null || left === "" || right === "") return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
}

function linkedQuestionId(link) {
  return (
    link?.questionUUID ||
    link?.questionUuid ||
    link?.QuestionUUID ||
    link?.question?.uuid ||
    null
  );
}

const setAnswerOptionQuestions = (question, _questionsArr, qo) => {
  let order = qo + 1;
  if (!question?.uuid) return { q: null, order: qo };
  question.questionOrder = order;
  if (hasSubQuestions.includes(question?.typeCode)) {
    (question.answerOptions || []).forEach((ao) => {
      if (!ao) return;
      ao.subQuestions = ao.subQuestions || [];
      const links = ao.answerOptionsQuestions || [];
      if (links.length > ao.subQuestions.length) {
        links.forEach((aoq) => {
          const linkedId = linkedQuestionId(aoq);
          const linked = _questionsArr.find((item) =>
            sameUuid(item?.uuid, linkedId),
          );
          if (!linked?.uuid) return;
          if (ao.subQuestions.some((item) => sameUuid(item?.uuid, linked.uuid))) {
            return;
          }
          const result = setAnswerOptionQuestions(
            { ...linked, questionOrder: order },
            _questionsArr,
            order,
          );
          order = result.order;
          if (result.q?.uuid) {
            ao.subQuestions.push(result.q);
          }
        });
      }
    });
  }
  return { q: question, order };
};

function markPlaced(questions, placed) {
  (questions || []).forEach((question) => {
    if (!question?.uuid) return;
    placed.add(String(question.uuid).toLowerCase());
    (question.answerOptions || []).forEach((option) =>
      markPlaced(option?.subQuestions, placed),
    );
  });
}

export const setFormQuestions = (_questionsArr) => {
  let qo = 0;
  const tempArr = [];
  (_questionsArr || []).forEach((question) => {
    if (!question?.uuid) return;
    if ((question.answerOptionsQuestions || []).length === 0) {
      const { q, order } = setAnswerOptionQuestions(
        question,
        _questionsArr,
        qo,
      );
      qo = order;
      if (q?.uuid) tempArr.push(q);
    }
  });

  const placed = new Set();
  markPlaced(tempArr, placed);
  (_questionsArr || []).forEach((question) => {
    if (!question?.uuid) return;
    if (placed.has(String(question.uuid).toLowerCase())) return;
    tempArr.push(question);
  });

  return tempArr;
};

export const setFormStructure = (rawForm) => {
  const structuredForm = rawForm;
  structuredForm.questionsList = rawForm.questions;
  const structuredQuestions = setFormQuestions(rawForm.questions);
  structuredForm.questions = structuredQuestions;
  return structuredForm;
};

export const recursiveOrderQuestions = (arr, aoUUID = null) => {
  arr.forEach((question, idx) => {
    question.order = idx;
    question.answerOptionUUID = aoUUID;
    question.answerOptions.forEach((answer) => {
      recursiveOrderQuestions(answer.subQuestions, answer.uuid);
    });
  });
  return arr;
};

export const getTargetPath = (targetItem) => {
  let { questionIdx } = targetItem;
  let targetObject = { ...targetItem };
  const targetPath = [targetObject];

  while (questionIdx === undefined) {
    const { parentObject } = targetObject;
    targetObject = parentObject;
    questionIdx = targetObject?.questionIdx;
    targetPath.push({ ...targetObject });
  }

  return targetPath;
};

export const checkPathsEqual = (addPath, removePath) => {
  for (let i = 0; i < addPath.length; i += 1) {
    if (
      addPath[i]?.answerIdx !== removePath[i].answerIdx ||
      addPath[i]?.subIdx !== removePath[i].subIdx ||
      addPath[i]?.questinoIdx !== removePath[i].questinoIdx
    ) {
      return false;
    }
  }

  return true;
};

export function getLocalDateTime(date) {
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  if (typeof date === "string") {
    return new Date(`${new Date(date).toLocaleString()} UTC`).toLocaleString(
      undefined,
      options,
    );
  }
  return date.toLocaleString(undefined, options);
}

export function isUUID(uuid) {
  const guidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(uuid);
}

export function capitalizeFirstLetter(string) {
  if (string.length === 0) {
    return string; // Safely handle empty strings
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// Local time formatting helper
export const formatLocalDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function isNumber(str) {
  if (typeof str === "number") return true;
  if (typeof str !== "string") return false;
  const trimmed = str.trim();
  return trimmed !== "" && !isNaN(+trimmed);
}

export function findUserGroup(groups, groupUUID) {
  if (!groupUUID) return null;
  const target = String(groupUUID).toLowerCase();
  return (
    (groups || []).find(
      (group) => group?.uuid && String(group.uuid).toLowerCase() === target,
    ) || null
  );
}

export function taskAssigneeLabel(task, groups) {
  if (!task?.groupUUID) return task?.userName || "";
  const group = findUserGroup(groups, task.groupUUID);
  const name = group?.name?.trim() || task.userName?.trim() || "";
  return name ? `Group: ${name}` : "Group:";
}

export function taskDecision(task) {
  if (Number(task?.type) !== approvalTaskType) return "";
  if (
    task?.status === approvalDecisions.approved ||
    task?.status === approvalDecisions.rejected
  ) {
    return task.status;
  }
  return "";
}

export function isTaskFinished(task) {
  return task?.status === "Complete" || Boolean(taskDecision(task));
}
